"use client";
import React, { useState, useEffect } from "react";
import { PropertyType } from "../../../../../types";

import {
  mockProperties,
  PropertyTypeKey,
} from "../../../constants/mockProperties";
import { useRouter } from "next/navigation";

export function getPropertyType(key: PropertyTypeKey): string {
  switch (key) {
    case "forRent":
      return "rent";
    case "forSale":
      return "sale";
    case "forShortlet":
      return "shortlet";
    default:
      throw new Error("Invalid property type");
  }
}

import Tabs from "./Herotabs";
import { getCurrencySign } from "src/app/lib/getCurrencySign";
import Skeleton from "react-loading-skeleton";
import Image from "next/image";
import { generateSlugWithId } from "src/app/serverUtils";
const Hero = ({ properties }: { properties: PropertyType[] }) => {
  const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true"; // Check if DEMO_MODE is true
  const isLoading = false;
  const realProperties = properties ? [...properties.slice(0, 4)] : [];
  const wrappedProperties = {
    forRent: realProperties,
    forSale: realProperties,
    forShortlet: realProperties,
  };
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<PropertyTypeKey>("forRent");
  const [index, setIndex] = useState(0);
  const currentProperties = isDemoMode ? mockProperties : wrappedProperties;
  const currentProperty = currentProperties[activeTab]?.length;
  const currentPropertyId = currentProperties[activeTab]?.[index]?.id;
  const propertyType = getPropertyType(activeTab);
  const slugWithId = generateSlugWithId(
    currentPropertyId,
    currentProperties[activeTab]?.[index]?.title
  );
  const baseUrl = currentPropertyId
    ? `/properties/${propertyType}/${slugWithId}`
    : "/"; // Safely handle potential undefined `currentPropertyId`
  useEffect(() => {
    setIndex(0); // Reset index when activeTab changes
  }, [activeTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % currentProperty);
    }, 4000);

    return () => clearInterval(interval);
  }, [currentProperty]);

  // Handle case where no data is available and not in demo mode
  if (!isDemoMode && isLoading) {
    return (
      <div className="wrapper flex gap-2">
        <Skeleton className="w-1/2" containerClassName="w-full" height={500} />
        <Skeleton className="w-1/2" containerClassName="w-full" height={500} />
      </div>
    );
  }

  if (!isDemoMode && !currentProperty && !isLoading) {
    return null;
  }

  return (
    <section className="text-white relative">
      <div className="show-case relative  lg:flex-col-reverse flex">
        <div className="gradient hidden lg:block absolute pointer-events-none z-[10] right-0 bottom-0 w-full"></div>
        <div className="gradient-bg lg:hidden absolute pointer-events-none z-[10] top-0 right-0 bottom-0 w-full h-full"></div>
        <div
          style={{
            backgroundImage: "url(/images/waves.webp)",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
          className="first h-auto pt-[5rem] pb-[2rem] relative overflow-hidden basis-[45%]"
        >
          <FirstSection
            baseUrl={baseUrl}
            {...currentProperties[activeTab]?.[index]} // Ensure safe access
          />
          <Tabs activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
        <div className="second relative basis-[60%]">
          <div
            onClick={() => {
              router.push(baseUrl);
            }}
            className="image cursor-pointer relative h-[520px] xs:h-[400px] w-full"
          >
            <Image
              height={500}
              width={500}
              priority
              fetchPriority="high"
              className="h-full absolute top-0 right-0 w-full object-cover"
              src={currentProperties[activeTab]?.[index]?.images[0]} // Safe access
              alt="Property Image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

interface FirstSectionProps extends PropertyType {
  baseUrl: string;
}

const FirstSection: React.FC<FirstSectionProps> = ({
  title,
  address,
  price,
  quotePrice,
  currency,
  quote,
  size,
  reviews,
  baseUrl,
  category,
}) => {
  const router = useRouter();
  const formattedCurrency = getCurrencySign(currency);
  return (
    <div className="hero-item z-[10] relative">
      <div className="gradient-bg hidden absolute pointer-events-none z-[10] top-0 right-0 bottom-0 w-full h-full"></div>
      <div className="first gap-5 flex">
        <span className="capitalize">{category}</span>
        <span>{size}</span>
        <span>{reviews?.length} reviews</span>
      </div>
      <div
        onClick={() => {
          router.push(baseUrl);
        }}
        className="text-4xl  mr-6 sm:mr-auto cursor-pointer hover:underline leading-9 truncate-2-lines mb-4"
      >
        {title}
      </div>
      <div className="address truncate text-xl mb-2">{address}</div>
      <span className="price text-4xl ">
        {formattedCurrency}
        {price?.toLocaleString()}
      </span>
      <div className="init">Total Package</div>
      <div className="price capitalize">
        {formattedCurrency}
        {quotePrice?.toLocaleString()} / {quote}
      </div>
    </div>
  );
};
